import * as Sentry from '@sentry/react';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { ConfigProvider } from 'antd';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Authenticate } from './components/Authenticate';
import { DefaultPage } from './components/DefaultPage';
import { ProtectedRoutes } from './components/ProtectedRoutes';
import AppLayout from './layouts/AppLayout';
import Account from './pages/Account';
import Comments from './pages/Comments';
import CompanyEditor from './pages/DataEditor/CompanyEditor';
import CompanyEventsEditor from './pages/DataEditor/CompanyEventsEditor';
import CompanyMerge from './pages/DataEditor/CompanyMerge';
import InsiderEventsEditor from './pages/DataEditor/InsiderEventsEditor';
import PeopleEditor from './pages/DataEditor/PeopleEditor';
import PersonMerge from './pages/DataEditor/PersonMerge';
import ProfileEdits from './pages/DataEditor/ProfileEdits';
import Exception from './pages/Exception';
import ContactExport from './pages/ImportExport/ContactExport';
import ContactImport from './pages/ImportExport/ContactImport';
import CrmExportTracks from './pages/ImportExport/CrmExportTracks';
import ReferrerCode from './pages/ReferrerCode';
import Reports from './pages/Reports';
import TeamEditor from './pages/TeamEditor';
import CreditsLog from './pages/UserEditor/CreditsLog';
import UserMappingEditor from './pages/UserEditor/UserMappingEditor';
import UsersEditor from './pages/UserEditor/UsersEditor';
import UserSetupResponse from './pages/UserEditor/UserSetupResponse';
import TermsAcceptance from './pages/UserEditor/TermsAcceptance';
import { requestWithAuth } from './services/api';
import SignedRefcodes from './pages/SignedRefcodes';
import AidOrganizationSubscription from './components/AidOrganizationSubscription';
import ApiUsagePage from './pages/ApiUsage';
import AidOrganization from './pages/AidOrganization';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const App = () => {
  const { isInitialized, session } = useStytchMemberSession();
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('');
  const [isNexisAdmin, setIsNexisAdmin] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (session) {
        const response = await requestWithAuth(
          'admin-user-type',
          'GET',
          null,
          null,
        );
        if (response?.results?.[0]) {
          setName(response.results[0].full_name);
          setUserType(response.results[0].user_type);
          if (response.results[0].nexis_admin === true) {
            setIsNexisAdmin(true);
          }
        }
      }
    };

    if (!isInitialized) {
      return;
    }

    fetchUserData();
  }, [session, isInitialized]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#00416d',
          borderRadius: 0,
          colorTextBase: '#55585f',
          colorLink: '#1a8ddb',
          fontFamily: 'Roboto, Helvetica Neue',
        },
        components: {
          Button: {
            boxShadow: 'none',
            controlOutline: 'none',
          },
          Table: {
            headerColor: 'var(--color-primary)',
          },
        },
      }}
    >
      <SentryRoutes>
        <Route
          path="/"
          element={
            <AppLayout
              userName={name}
              userType={userType}
              isNexisAdmin={isNexisAdmin}
            />
          }
        >
          <Route index element={<DefaultPage />} />
          <Route path="/authenticate" element={<Authenticate />} />
          <Route element={<ProtectedRoutes />}>
            <Route path="data-editor">
              <Route path="vault" element={<PeopleEditor />} />
              <Route path="org-events" element={<CompanyEventsEditor />} />
              <Route path="insider-events" element={<InsiderEventsEditor />} />
              <Route path="company" element={<CompanyEditor />} />
              <Route path="company-merge" element={<CompanyMerge />} />
              <Route path="person-merge" element={<PersonMerge />} />
              <Route path="profile-edits" element={<ProfileEdits />} />
              <Route index element={<Navigate to="/data-editor/vault" />} />
            </Route>
            <Route path="user-editor">
              <Route
                path="users"
                element={<UsersEditor isNexisAdmin={isNexisAdmin} />}
              />
              <Route path="users-mapping" element={<UserMappingEditor />} />
              <Route path="credits-log" element={<CreditsLog />} />
              <Route
                path="user-setup-response"
                element={<UserSetupResponse />}
              />
              <Route path="terms-acceptance" element={<TermsAcceptance />} />
              <Route index element={<Navigate to="/user-editor/users" />} />
            </Route>

            <Route
              path="referrercode"
              element={<ReferrerCode isNexisAdmin={isNexisAdmin} />}
            />
            <Route path="comments" element={<Comments />} />
            <Route path="api-usage" element={<ApiUsagePage />} />
            <Route
              path="team-editor"
              element={<TeamEditor isNexisAdmin={isNexisAdmin} />}
            />
            <Route
              path="aid-organization"
              element={<AidOrganization isNexisAdmin={isNexisAdmin} />}
            />
            <Route path="import-export">
              <Route
                path="contact-exports"
                element={<ContactExport isNexisAdmin={isNexisAdmin} />}
              />
              <Route
                path="contact-imports"
                element={<ContactImport isNexisAdmin={isNexisAdmin} />}
              />
              <Route path="crm-export-tracks" element={<CrmExportTracks />} />
              <Route
                index
                element={<Navigate to="/import-export/contact-exports" />}
              />
            </Route>
            <Route path="reports" element={<Reports />} />
            <Route
              path="aid-organization-subscription"
              element={
                <AidOrganizationSubscription isNexisAdmin={isNexisAdmin} />
              }
            />
            <Route path="signed-refcodes" element={<SignedRefcodes />} />
            <Route path="account">
              <Route path="settings/base" element={<Account name={name} />} />
              <Route
                path="settings"
                element={<Navigate to="/account/settings/base" />}
              />
              <Route index element={<Navigate to="/account/settings/base" />} />
            </Route>
            <Route path="*" element={<Exception />} />
          </Route>
        </Route>
        <Route path="/exception" element={<Exception />} />
      </SentryRoutes>
    </ConfigProvider>
  );
};

export default App;
